import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first } from 'rxjs';
import { AccessControl } from '@pncl/common-models';
import { User } from './models/user';
import { NavService } from './services/nav.service';
import { Feature, AclService } from './services/acl.service';
import {
  AnalyticsDashboardV2Variable,
  FLAGS,
  FlagsService,
  OmniSearchVariable,
} from './services/flags.service';
import { UserService } from './services/user.service';
import { LANDING_URL, URL_CONSTANTS } from './common/utils/url';
import { AuthService } from './services/auth.service';
import { FeatureManagementService } from './services/feature-management.service';
import { AccountEntryService } from './account/account-entry/account-entry.service';
import { CapabilityTierService } from './services/capability-tier.service';
import { RecordingDetailsService } from './services/recording-details.service';
import { AnalyticsSessionsDetailsService } from './analytics-sessions/analytics-sessions-details.service';
import Capability = AccessControl.Capability;

// Map of route patterns with their feature flags and flag type
const ROUTE_FLAGS: {
  [key: string]: { flag: FLAGS; isDisableFlag: boolean };
} = {
  // Example: '/schedule': { flag: FLAGS.ENABLE_SCHEDULE, isDisableFlag: false },
  '/sessions': { flag: FLAGS.ENABLE_NEW_SESSIONS_FLOW, isDisableFlag: false },
  '/analytics/recordings': { flag: FLAGS.ENABLE_NEW_SESSIONS_FLOW, isDisableFlag: true },
};

const ROUTE_CAPABILITY_MAP: Record<string, Capability> = {
  '/content/files': Capability.PENCIL_FILES,
  '/content': Capability.LMS,
  '/messages': Capability.GLOBAL_MESSAGING,
  '/alerts': Capability.CONTROL_CENTER_ALERTS,
};

const ROUTE_ACL_MAP: Record<string, Feature[]> = {
  '/alerts': [Feature.isAdmin, Feature.isSiteAdmin],
};

// Routes that can be accessed by non-signed-in users
const PUBLIC_ROUTES: string[] = ['content/view/', 'techcheck'];

@UntilDestroy()
@Injectable()
export class UserActivate implements CanActivate {
  user?: User;
  Features = Feature;

  constructor(
    private router: Router,
    private navService: NavService,
    private userService: UserService,
    public aclService: AclService,
    private flagsService: FlagsService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private accountEntryService: AccountEntryService,
    private capabilityTierService: CapabilityTierService,
    private featureManagementService: FeatureManagementService,
    private recordingDetailsService: RecordingDetailsService,
    private analyticsSessionsDetailsService: AnalyticsSessionsDetailsService,
  ) {}

  private isPublicRoute(url: string): boolean {
    return PUBLIC_ROUTES.some((route) => url.includes(route));
  }

  private shouldBlockRouteByCapability(url: string): boolean {
    const lowerCaseUrl = url.toLowerCase();

    if (lowerCaseUrl.startsWith('/schedule')) {
      return !this.featureManagementService.isSchedulingEnabled();
    }

    // Loop through the capability map looking for matches
    for (const [routePattern, capability] of Object.entries(ROUTE_CAPABILITY_MAP)) {
      if (lowerCaseUrl.startsWith(routePattern)) {
        // Block if capability is not enabled
        return !this.capabilityTierService.shouldEnableCapability(capability);
      }
    }

    return false;
  }

  private shouldBlockRouteByFeatureFlag(url: string): boolean {
    // Normalize the URL to lowercase for case-insensitive comparison
    const lowerCaseUrl = url.toLowerCase();

    for (const [routePattern, config] of Object.entries(ROUTE_FLAGS)) {
      // Compare with the lowercase route pattern
      if (lowerCaseUrl === routePattern.toLowerCase()) {
        const flagEnabled = this.flagsService.isFlagEnabled(config.flag);
        // For disable flags (DISABLE_*): block if flag is enabled
        // For enable flags (ENABLE_*): block if flag is disabled
        return config.isDisableFlag ? flagEnabled : !flagEnabled;
      }
    }
    // No matching pattern found, allow access
    return false;
  }

  private shouldBlockRouteByAcl(url: string, user: User): boolean {
    const lowerCaseUrl = url.toLowerCase();

    for (const [routePattern, allowedFeatures] of Object.entries(ROUTE_ACL_MAP)) {
      if (lowerCaseUrl.startsWith(routePattern)) {
        return !this.aclService.isAnyFeatureAllowed(user, allowedFeatures);
      }
    }

    return false;
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    this.userService.appLoading.next(false);

    // Only allow public question view for non-signed in users.
    if (this.isPublicRoute(state.url)) {
      return true;
    }

    this.user = this.userService.user.value?.user || (await this.navService.loading);

    // If this is a single recording view route, check if recording is public
    if (this.recordingDetailsService.isSingleRecordingViewRoute(state.url)) {
      const recordingId = this.recordingDetailsService.extractRecordingIdFromUrl(state.url);

      if (recordingId) {
        // Check if recording details are already loaded
        const cachedState = this.recordingDetailsService.getCachedRecordingDetails();
        // If recording is public and user is not authenticated, bypass user guard
        const firebaseUser = this.authService.getCurrentUser();
        if (!firebaseUser && cachedState?.isPublic && cachedState.recording) {
          return true;
        }
      }
    }

    if (this.analyticsSessionsDetailsService.isSingleSessionViewRoute(state.url)) {
      const sessionId = this.analyticsSessionsDetailsService.extractSessionIdFromUrl(state.url);

      if (sessionId) {
        const cachedState = this.analyticsSessionsDetailsService.getCachedSessionDetails();
        const firebaseUser = this.authService.getCurrentUser();
        if (!firebaseUser && cachedState?.isPublic && cachedState.session) {
          return true;
        }
      }
    }

    if (!this.user) {
      return false;
    }
    // Check if route should be blocked based on capabilities
    if (this.shouldBlockRouteByCapability(state.url)) {
      this.router.navigate([`/${LANDING_URL}`]);
      return false;
    }

    // Check if route should be blocked based on feature flags
    if (this.shouldBlockRouteByFeatureFlag(state.url)) {
      this.router.navigate([`/${LANDING_URL}`]);
      return false;
    }

    // Check if route should be blocked based on ACL features
    if (this.shouldBlockRouteByAcl(state.url, this.user)) {
      this.router.navigate([`/${LANDING_URL}`]);
      return false;
    }

    // Check guest (institution-less) users for self serve.
    if (this.user && this.aclService.isGuest(this.user)) {
      // if path != account
      // redirect verifed only email/password self serve users to /space/new
      // else show verify page
      if (!state.url.includes('account') || state.url.includes('user_info')) {
        let result = true;
        this.authService.firebaseUser
          .pipe(untilDestroyed(this))
          .pipe(first())
          .subscribe((user) => {
            // block accounts with only one provider: email-password from accessing spaces until verified
            // otherwise this would mark facebook and possibly other providers as unverified
            if (this.accountEntryService.isOnlyPasswordProviderAndEmailUnverified(user)) {
              result = false;
              if (!state.url.includes('login')) {
                this.router.navigate([URL_CONSTANTS.LOGIN], {
                  queryParams: {
                    continueUrl: state.url,
                  },
                  queryParamsHandling: 'merge',
                  fragment: this.activatedRoute.snapshot.fragment || '',
                });
              }
              this.accountEntryService.goToVerificationPage(user?.email);
            }
            return result;
          });
      }
    }

    // Enable analytics only for parent users
    if (this.user && this.aclService.isParent(this.user)) {
      if (
        (state.url.includes('/content/course') || state.url.includes('/content/worksheets')) &&
        !state.url.includes('analytics')
      ) {
        const { id } = route.params;
        if (id) {
          this.router.navigate([`/content/course/questions/${id}/analytics`]);
        } else {
          this.router.navigate(['/content']);
        }
        return false;
      }
    }

    if (state.url.includes('content/create')) {
      if (
        state.url.includes('content/create/0/') &&
        this.aclService.isAllowed(this.user, this.Features.create_question)
      ) {
        return true;
      } else if (this.aclService.isAllowed(this.user, this.Features.edit_question)) {
        return true;
      }
    } else if (state.url.includes('content/notes/create')) {
      if (
        state.url.includes('/content/create/notes/0/') &&
        this.aclService.isAllowed(this.user, this.Features.create_note)
      ) {
        return true;
      } else if (this.aclService.isAllowed(this.user, this.Features.edit_note)) {
        return true;
      }
    } else if (state.url.includes('admin')) {
      if (this.aclService.isAllowed(this.user, this.Features.AdminPage)) {
        return true;
      }
    } else if (state.url.includes('practice')) {
      if (state.url.includes('summary')) {
        if (
          this.aclService.isAllowed(this.user, this.Features.Practice) ||
          this.aclService.isAllowed(this.user, this.Features.ContentAnalytics) ||
          this.aclService.isAllowed(this.user, this.Features.PracticeAnalytics)
        ) {
          return true;
        }
      }
      if (this.aclService.isAllowed(this.user, this.Features.Practice)) {
        return true;
      }
    } else if (state.url.includes('profiles')) {
      if (this.aclService.isAllowed(this.user, this.Features.ProfilesPage)) {
        return true;
      }
    } else if (state.url.startsWith('/content/files')) {
      if (this.aclService.isAllowed(this.user, this.Features.view_resource)) {
        return true;
      }
    } else if (state.url.includes('worksheets')) {
      if (this.aclService.isAllowed(this.user, this.Features.view_set)) {
        return true;
      }
    } else if (state.url.includes('/course/settings')) {
      if (this.aclService.isAllowed(this.user, this.Features.edit_course)) {
        return true;
      }
    } else if (state.url.includes('/content/course/new')) {
      if (this.aclService.isAllowed(this.user, this.Features.create_course)) {
        return true;
      }
    } else if (state.url.startsWith('/schedule')) {
      if (this.aclService.isAllowed(this.user, this.Features.view_schedule_page)) {
        return true;
      }
    } else if (state.url.startsWith('/messages')) {
      if (this.aclService.isAllowed(this.user, this.Features.chat)) {
        return true;
      }
    } else if (state.url.startsWith('/omnisearch')) {
      // Only where search works: the API answers 403 unless the `omnisearch` flag's
      // `serving` variable is on for the user.
      if (this.flagsService.isOmniSearchEnabled(OmniSearchVariable.Serving)) {
        return true;
      }
      this.router.navigate([`/${LANDING_URL}`]);
      return false;
    } else if (
      state.url.startsWith('/settings') ||
      state.url.startsWith('/notifications') ||
      state.url.startsWith('/content') ||
      state.url.startsWith('/pencilcam') ||
      state.url.startsWith('/recording') ||
      state.url.startsWith('/next') ||
      state.url.startsWith('/alerts') ||
      state.url.startsWith('/home') ||
      // Slack link/claim pages. Who may link or claim is the API's decision.
      state.url.startsWith('/slack/') ||
      state.url.startsWith(`/${URL_CONSTANTS.SPACES}`) ||
      state.url.startsWith('/sessions')
    ) {
      return true;
    } else if (
      state.url.startsWith('/analytics') &&
      this.aclService.isAdminOrSiteAdmin(this.user) &&
      this.flagsService.isFlagEnabled(FLAGS.PROJECT_SAM)
    ) {
      return (
        (!!this.flagsService.featureFlagsVariables.project_sam.attendance_report &&
          state.url.includes('/attendance')) ||
        (!!this.flagsService.featureFlagsVariables.project_sam.analytics_report &&
          state.url.includes('/engagement')) ||
        (state.url.includes('/recordings') &&
          !this.flagsService.isFlagEnabled(FLAGS.ENABLE_NEW_SESSIONS_FLOW)) ||
        (state.url.includes('/sites') &&
          (!state.url.includes('/sites/coaching') ||
            this.flagsService.isAnalyticsDashboardV2Enabled(
              AnalyticsDashboardV2Variable.SitesCoaching,
            ))) ||
        (state.url.includes('/teachers') &&
          this.flagsService.isAnalyticsDashboardV2Enabled(
            AnalyticsDashboardV2Variable.TeachersCoaching,
          ))
      );
    }

    // No this.user check needed: the early `if (!this.user) return false` above
    // ensures only authenticated users reach this redirect logic.
    if (this.aclService.isGuest(this.user)) {
      this.router.navigate([`/${LANDING_URL}`]);
    } else if (this.router.url === '/') {
      this.router.navigateByUrl(`/${LANDING_URL}`);
    } else {
      return true;
    }

    return false;
  }
}
